import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  formatBulanID,
  formatRupiahFull,
  formatRupiahSigned,
  formatTanggalID,
  currentBulan,
  shiftBulan,
} from "@/lib/format";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowLeftRight,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

export default function MutasiRekening() {
  const { id } = useParams();
  const nav = useNavigate();
  const [bulan, setBulan] = useState(currentBulan());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rekening/${id}/mutasi?bulan=${bulan}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, bulan]);

  const grouped = useMemo(() => {
    if (!data?.transaksi) return [];
    const g = data.transaksi.reduce((acc, t) => {
      (acc[t.tanggal] = acc[t.tanggal] || []).push(t);
      return acc;
    }, {});
    return Object.keys(g)
      .sort((a, b) => b.localeCompare(a))
      .map((k) => {
        let masuk = 0,
          keluar = 0;
        for (const t of g[k]) {
          if (t.direction === "masuk") masuk += t.nominal;
          else if (t.direction === "keluar") keluar += t.nominal;
        }
        return { tanggal: k, items: g[k], masuk, keluar };
      });
  }, [data]);

  const isCurrent = bulan === currentBulan();

  return (
    <div className="pb-6" data-testid="mutasi-page">
      {/* Header */}
      <div
        className="px-4 pt-5 pb-3 sticky top-0 z-10"
        style={{ backgroundColor: "#f8fafc" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => nav("/rekening")}
            data-testid="mutasi-back-btn"
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center tap"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="min-w-0">
            {data?.rekening ? (
              <>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">
                  {data.rekening.bank}
                </div>
                <div className="text-sm font-extrabold text-slate-900 truncate">
                  {data.rekening.nama}
                </div>
              </>
            ) : (
              <div className="text-xs text-slate-500">Memuat...</div>
            )}
          </div>
        </div>

        {/* Bulan navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setBulan(shiftBulan(bulan, -1))}
            data-testid="mutasi-bulan-prev"
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center tap"
          >
            <ChevronLeft size={18} />
          </button>
          <div
            className="text-base font-extrabold text-slate-900"
            data-testid="mutasi-bulan-label"
          >
            {formatBulanID(bulan)}
          </div>
          <button
            disabled={isCurrent}
            onClick={() => setBulan(shiftBulan(bulan, 1))}
            data-testid="mutasi-bulan-next"
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center tap disabled:opacity-40"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Summary card */}
      {loading || !data ? (
        <div className="px-4 text-xs text-slate-500 py-6 text-center">
          Memuat...
        </div>
      ) : (
        <>
          <div className="px-4">
            <div
              className="rounded-2xl p-4 text-white shadow-lg"
              style={{ backgroundColor: "#1F4E5F" }}
              data-testid="mutasi-summary-card"
            >
              <SummaryRow
                label="Saldo awal bulan"
                value={formatRupiahFull(data.saldo_awal)}
              />
              <SummaryRow
                label="Total masuk"
                value={`+${formatRupiahFull(data.total_masuk)}`}
                valueColor="#A7D5A0"
                icon={<TrendingUp size={12} />}
              />
              <SummaryRow
                label="Total keluar"
                value={`-${formatRupiahFull(data.total_keluar)}`}
                valueColor="#F4A6A6"
                icon={<TrendingDown size={12} />}
              />
              <div className="border-t border-white/20 mt-2 pt-2">
                <SummaryRow
                  label="Saldo akhir"
                  value={formatRupiahFull(data.saldo_akhir)}
                  bold
                />
              </div>
            </div>
          </div>

          {/* Transactions grouped by date */}
          <div className="px-4 mt-4">
            {grouped.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center text-xs text-slate-500 border border-slate-100">
                Tidak ada mutasi di {formatBulanID(bulan)}.
              </div>
            ) : (
              <div className="space-y-4" data-testid="mutasi-list">
                {grouped.map(({ tanggal, items, masuk, keluar }) => (
                  <div key={tanggal}>
                    <div className="flex items-baseline justify-between mb-1.5 px-1">
                      <div className="text-xs font-bold text-slate-700">
                        {formatTanggalID(tanggal)}
                      </div>
                      <div className="text-[11px] font-semibold">
                        {masuk > 0 && (
                          <span style={{ color: "#375623" }}>
                            {formatRupiahSigned(masuk, "+")}
                          </span>
                        )}
                        {masuk > 0 && keluar > 0 && (
                          <span className="text-slate-400"> / </span>
                        )}
                        {keluar > 0 && (
                          <span style={{ color: "#C00000" }}>
                            {formatRupiahSigned(keluar, "-")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                      {items.map((t, i) => {
                        const isTransfer = t.tipe === "transfer";
                        const dir = t.direction;
                        return (
                          <div
                            key={t.id}
                            className={`px-3 py-2.5 flex items-center justify-between gap-2 ${
                              i > 0 ? "border-t border-slate-100" : ""
                            }`}
                            style={
                              isTransfer
                                ? { backgroundColor: "#DEEAF1" }
                                : undefined
                            }
                          >
                            <div className="min-w-0 flex-1 flex items-start gap-2">
                              <div
                                className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                                style={{
                                  backgroundColor: isTransfer
                                    ? "#2E75B6"
                                    : dir === "masuk"
                                    ? "#375623"
                                    : "#C00000",
                                }}
                              >
                                {isTransfer ? (
                                  <ArrowLeftRight size={11} strokeWidth={2.4} />
                                ) : dir === "masuk" ? (
                                  <TrendingUp size={11} strokeWidth={2.4} />
                                ) : (
                                  <TrendingDown size={11} strokeWidth={2.4} />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-slate-900 truncate">
                                  {t.display_label || t.deskripsi}
                                </div>
                                {t.is_debt_payment && (
                                  <div
                                    className="text-[9px] font-bold"
                                    style={{ color: "#B45309" }}
                                  >
                                    · Bayar hutang
                                  </div>
                                )}
                              </div>
                            </div>
                            <div
                              className="text-xs font-bold whitespace-nowrap"
                              style={{
                                color:
                                  dir === "masuk"
                                    ? "#375623"
                                    : dir === "keluar"
                                    ? "#C00000"
                                    : "#1F4E5F",
                              }}
                            >
                              {dir === "masuk"
                                ? "+"
                                : dir === "keluar"
                                ? "-"
                                : ""}
                              {formatRupiahFull(t.nominal)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryRow({ label, value, valueColor, icon, bold }) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-1.5 text-[11px] text-white/80">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={`text-sm ${bold ? "font-extrabold" : "font-bold"}`}
        style={{ color: valueColor || "white" }}
      >
        {value}
      </div>
    </div>
  );
}
